import React, { FC } from 'react';
import {
    Box,
    Button,
    Grid,
    ListItemIcon,
    Menu,
    MenuItem,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import styles from './PageHeaderComponent.module.scss';
import boddVariables from '../../../../variables.module.scss';

export interface HeaderAction {
    text: string;
    onClick: () => void;
    icon?: React.ReactNode;
    disabled?: boolean;
    colour?: 'success' | 'primary' | 'secondary' | 'error' | 'info' | 'warning' | undefined;
    indicateBoddOnly?: boolean;
    secondaryStyleOnly?: boolean;
}

interface PageHeaderComponentProps {
    title: string;
    breadcrumbs?: React.ReactNode;
    // The last action in the actions array is the primary action, all others are secondary actions
    actions?: HeaderAction[];
    customAction?: React.ReactNode;
}

const PageHeaderComponent: FC<PageHeaderComponentProps> = (props) => {
    const { title, breadcrumbs, customAction, actions } = props;
    const theme = useTheme();
    const primaryAction = React.useMemo(() => (customAction ? undefined : actions?.[actions.length - 1]), [actions]);
    const secondaryActions = React.useMemo(() => {
        if (!actions) {
            return undefined;
        }
        if (customAction) {
            return actions?.slice(0);
        }
        if (actions.length > 1) {
            return actions.slice(0, -1);
        }
        return undefined;
    }, [actions]);
    const isSmallLayout = useMediaQuery(theme.breakpoints.down('sm'));
    const isMediumLayout = useMediaQuery(theme.breakpoints.only('sm'));
    const isLargeLayout = !isSmallLayout && !isMediumLayout;
    const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(null);

    const openMenu = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setMenuAnchorEl(event.currentTarget);
    };

    const closeMenu = () => {
        setMenuAnchorEl(null);
    };

    return (
        <div className={styles.PageHeaderComponent} data-testid="PageHeaderComponent">
            <Box sx={{ my: 4 }}>
                <Grid
                    container
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                >
                    <Grid size={{ xs: 12 }}>
                        {breadcrumbs}
                    </Grid>
                    <Grid>
                        <Typography variant="h2">{title}</Typography>
                    </Grid>
                    <Grid size={{ xs: isSmallLayout ? 12 : undefined }}>
                        <Stack
                            direction={isSmallLayout ? 'row-reverse' : 'row'}
                            justifyContent="flex-end"
                            alignItems="center"
                            spacing={2}
                            mt={isSmallLayout ? 3 : 0}
                        >
                            {isLargeLayout && secondaryActions?.map((action) => (
                                <Button
                                    sx={{
                                        backgroundColor: action.indicateBoddOnly ? `${boddVariables.brandPink}26` : 'undefined',
                                    }}
                                    key={action.text}
                                    startIcon={action.icon}
                                    onClick={action.onClick}
                                    variant="text"
                                    disabled={action.disabled}
                                    color={action.colour ?? 'inherit'}
                                >
                                    {action.text}
                                </Button>
                            ))}
                            {!isLargeLayout && secondaryActions && secondaryActions.length > 0 && (
                                <Button
                                    startIcon={<MoreHorizIcon />}
                                    variant="text"
                                    onClick={(e) => openMenu(e)}
                                >
                                    More Actions
                                </Button>
                            )}
                            {primaryAction && (
                                <Button
                                    startIcon={primaryAction.icon}
                                    onClick={primaryAction.onClick}
                                    disabled={primaryAction.disabled}
                                    variant={primaryAction.secondaryStyleOnly ? 'text' : 'contained'}
                                    color={primaryAction.colour ?? (primaryAction.secondaryStyleOnly ? 'inherit' : 'primary')}
                                    sx={{
                                        backgroundColor: !primaryAction.colour && primaryAction.indicateBoddOnly ? `${boddVariables.brandPink}26` : 'undefined',
                                    }}
                                >
                                    {primaryAction.text}
                                </Button>
                            )}
                            {customAction}
                        </Stack>
                    </Grid>
                </Grid>
            </Box>
            <Menu
                anchorEl={menuAnchorEl}
                open={!!menuAnchorEl}
                onClose={closeMenu}
            >
                {secondaryActions?.map((a) => (
                    <MenuItem
                        key={a.text}
                        onClick={a.onClick}
                        disabled={a.disabled}
                        sx={{
                            color: a.colour ? `${a.colour}.main` : boddVariables.grey600,
                            backgroundColor: a.indicateBoddOnly ? `${boddVariables.brandPink}26` : 'undefined',
                        }}
                    >
                        {a.icon && (
                            <ListItemIcon sx={{ color: a.colour ? `${a.colour}.main` : boddVariables.grey600 }}>
                                {a.icon}
                            </ListItemIcon>
                        )}
                        {a.text}
                    </MenuItem>
                ))}
            </Menu>
        </div>
    );
};

export default PageHeaderComponent;

PageHeaderComponent.defaultProps = {
    breadcrumbs: undefined,
    customAction: undefined,
    actions: undefined,
};
